<script setup lang="ts">
import type { BTableSortBy, TableField, TableItem } from 'bootstrap-vue-next';
import { BAccordion, BAccordionItem, BPagination, BTooltip } from 'bootstrap-vue-next';
import { ref, computed, useId } from 'vue';

import TableWithBusyState from '~/common/components/TableWithBusyState.vue';
import { numberFormatter } from '~/common/FieldFormats';

const props = withDefaults(defineProps<{
  fields?: TableField<TableItem>[];
  items?: TableItem[];
  busy?: boolean;
  title: string;
  sortKey: string;
  totalPoints?: number;
  scoringTooltip?: string;
  id?: string;
}>(), {
  fields: () => [],
  items: undefined,
  totalPoints: undefined,
  scoringTooltip: undefined,
  id: undefined,
});

const sortBy = ref<BTableSortBy[]>([
  {
    key: props.sortKey,
    order: 'desc',
  },
]);
const perPage = ref(10);
const currentPage = ref(1);
const rows = computed(() => props.items?.length);

const elementId = computed(() => {
  return props.id || props.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
});

// Add sortable attribute to all columns by default, unless overwritten in fields
const sortableFields = computed(() => {
  return props.fields.map(field => ({
    ...field,
    sortable: field.sortable !== false,
  }));
});

const isAccordionVisible = computed(() => {
  return (rows.value ?? 0) > 0;
});

const pointsLabel = computed(() => `${numberFormatter(props.totalPoints)} points`);
const tooltipId = `scoring-tooltip-${useId()}`;
</script>

<template>
  <BAccordion
    :id="elementId"
    class="my-2 accordion-table"
  >
    <BAccordionItem :visible="isAccordionVisible">
      <template #title>
        <h2 class="d-flex align-items-center justify-content-between w-100">
          <span>
            {{ title }}
            <span
              v-if="totalPoints !== undefined"
              class="fw-normal"
            >(<span
              v-if="scoringTooltip"
              :id="tooltipId"
              tabindex="0"
              :aria-label="`${pointsLabel}. ${scoringTooltip}`"
              class="with-help-text"
              @click.stop
            >{{ pointsLabel }}</span><template v-else>{{ pointsLabel }}</template>)</span>
            <BTooltip
              v-if="scoringTooltip"
              :target="tooltipId"
              teleport-to="body"
            >
              {{ scoringTooltip }}
            </BTooltip>
          </span>
          <slot name="headerButtons" />
        </h2>
      </template>
      <TableWithBusyState
        v-model:sort-by="sortBy"
        :fields="sortableFields"
        :items="items"
        :busy="busy"
        :per-page="perPage"
        :current-page="currentPage"
        show-empty
        class="mb-0"
      >
        <template
          v-for="(_, name) in $slots"
          :key="name"
          #[name]="slotData"
        >
          <slot
            :name="name"
            v-bind="slotData"
          />
        </template>
      </TableWithBusyState>
      <div class="row align-items-center g-0 p-2">
        <div class="col d-none d-sm-block" />
        <div class="col-12 col-sm d-flex justify-content-center">
          <BPagination
            v-if="rows && rows > perPage"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            first-number
            last-number
            class="mb-2 mb-sm-0"
          />
        </div>
        <div class="col-12 col-sm text-end text-muted">
          Total items: {{ numberFormatter(items?.length ?? 0) }}
        </div>
      </div>
    </BAccordionItem>
  </BAccordion>
</template>
